import PropTypes from 'prop-types';
import { Fragment, useState, useEffect, MutableRefObject } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Flex, Text, ThemeUIStyleObject } from 'theme-ui';
import { useProduct } from '@chordcommerce/react-autonomy';
import { logSentryError } from '~/sentry';
import clampBuilder from '~/utils/clamp-builder';
import WaitlistSignup from './WaitlistSignup';
import { serializeProductOrKit } from '~/utils/serialize';
import { ProductWithStock } from '~/hooks/components/use-product-availability';
import { useTranslate } from '~/hooks/utils/use-translate';
import { useCustomAnalytics } from '~/hooks/utils/customAnalytics';

type Props = {
  sku: string | null | undefined;
  product: ProductWithStock | Queries.KitPageFragmentFragment | Queries.BundlePageFragment;
  comingSoon: boolean | null | undefined;
  outOfStock: boolean | null | undefined;
  discontinued: boolean | null | undefined;
  OOSNotify: MutableRefObject<Queries.NotificationFragmentFragment | null>;
  comingSoonNotify: MutableRefObject<Queries.NotificationFragmentFragment | null>;
  forwardSx?: ThemeUIStyleObject;
};
type FormValues = {
  email: string;
};
const ProductOutOfStock = ({
  sku,
  product,
  comingSoon,
  outOfStock,
  discontinued,
  OOSNotify,
  comingSoonNotify,
  forwardSx = {},
}: Props) => {
  const translate = useTranslate();
  const { createStockRequest } = useProduct();
  const { trackEmailCaptured } = useCustomAnalytics();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [oosError, setOosError] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    setSubmitted(false);
  }, [sku]);

  const innerHtml = comingSoon
    ? comingSoonNotify?.current?.description?.childMarkdownRemark?.html
    : outOfStock && !discontinued
    ? OOSNotify?.current?.description?.childMarkdownRemark?.html
    : null; // no notifications only if discontinued

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    try {
      await createStockRequest({ email: data.email, product: serializeProductOrKit(product, sku) });
      await trackEmailCaptured({
        email: data.email,
        locationDetail: 'stock_request_product_page',
      });
      setSubmitted(true);
      setOosError(false);
    } catch (error) {
      setOosError(true);
      logSentryError(error, { source: 'ProductOutOfStock', sku });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex sx={{ flexDirection: 'column', py: '1.6rem', ...forwardSx }}>
      <Fragment>
        {innerHtml && (
          <Text
            variant="copy"
            sx={{
              strong: { textTransform: 'uppercase', fontWeight: '500', mb: '1.6rem' },
              p: { m: 0 },
              marginBottom: '2.4rem',
              br: { content: '" "', display: 'block', padding: '1rem' },
            }}
            dangerouslySetInnerHTML={{
              __html: innerHtml,
            }}
          ></Text>
        )}

        {(comingSoon || (outOfStock && !discontinued)) && (
          <Box as="form" data-testid="form" onSubmit={handleSubmit(onSubmit)}>
            <Flex
              sx={{
                flexDirection: ['column'],
                alignItems: ['center', 'flex-start'],
              }}
            >
              <Box
                sx={{
                  marginRight: [0, '1rem'],
                  marginBottom: ['1rem', 0],
                  width: '100%',
                }}
              >
                {submitted || loading ? (
                  <Text
                    sx={{
                      variant: 'text.h5',
                      px: '0.4rem',
                      fontFamily: 'heading',
                      fontSize: [
                        `${clampBuilder(320, 414, 0.8, 1.1)} !important`,
                        `${clampBuilder(873, 1536, 0.9, 1.2)} !important`,
                      ],
                    }}
                  >
                    {submitted ? 'Thanks, we will notify you soon.' : loading && 'Loading'}
                  </Text>
                ) : (
                  <WaitlistSignup
                    {...register('email', {
                      required: true,
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: translate('validation.invalid_email'),
                      },
                    })}
                  />
                )}
              </Box>
            </Flex>
            {errors && errors['email'] && errors['email'].type === 'required' && (
              <Text color="errorDark">{translate('validation.required_field')}</Text>
            )}
            {errors && errors['email'] && errors['email'].type === 'pattern' && (
              <Text color="errorDark">{translate('validation.invalid_email')}</Text>
            )}
            {oosError && <Text color="errorDark">{translate('error.api.default')}</Text>}
          </Box>
        )}
      </Fragment>
    </Flex>
  );
};

ProductOutOfStock.propTypes = {
  sku: PropTypes.string.isRequired,
  discontinued: PropTypes.bool,
};

export default ProductOutOfStock;
